import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API = '/api';

// Enable cookies with cross-origin requests
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('besume_token'));
  const [loading, setLoading] = useState(true);
  const profilePollRef = useRef(null);
  const lastQueueStatus = useRef(null);

  // Fetch CSRF token for cookie-based auth
  const fetchCsrfToken = async () => {
    try {
      const res = await axios.get(`${API}/csrf-token`);
      axios.defaults.headers.common['X-CSRF-Token'] = res.data.csrf_token;
    } catch (e) {
      // Not authenticated yet, will fetch after login
    }
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      // Try cookie-based auth
      fetchCsrfToken().then(() => fetchUser());
    }
  }, [token]);

  // Refresh user object when window regains focus (handles email verification in separate tab)
  useEffect(() => {
    const handleFocus = () => {
      if (token) {
        fetchUser();
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      await fetchCsrfToken();
      return response.data.user;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
      return null;
    } finally {
      setLoading(false);
    }
  };

  // V5.19.3 — Poll for profile generation completion. Uses the backend's
  // reveal_pending flag (set by /wizard/status, cleared by
  // /wizard/mark-reveal-shown) as the source of truth for whether the
  // celebration UX has already fired for this generation. This replaces
  // the V5.19.2 localStorage-based dedup which re-fired on every hard
  // navigation (Fix 9).
  useEffect(() => {
    if (!user) return;

    const fireCompletionUX = async (revealPending) => {
      // Mark the reveal as shown BEFORE navigation/toast so downstream polls
      // on the celebration page (or any navigation) see reveal_pending=false
      // and skip the UX. Idempotent — safe if already marked.
      try { await axios.post(`${API}/wizard/mark-reveal-shown`); } catch {}

      // If backend reports reveal was already shown, this is a subsequent
      // page load for a user who already saw it — do nothing.
      if (!revealPending) return;

      if (user.slug) {
        window.location.href = `/celebration/${user.slug}`;
      } else {
        // First completion but slug missing — sticky toast fallback.
        toast.success('Your Besume draft is ready', {
          action: {
            label: 'Open',
            onClick: () => { window.location.href = '/dashboard'; },
          },
          duration: Infinity,
        });
      }
    };

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/wizard/status`);
        const status = res.data.status;
        const revealPending = res.data.reveal_pending === true;

        if (status === 'completed') {
          const alreadyHandled = lastQueueStatus.current === 'completed_handled';
          if (!alreadyHandled) {
            lastQueueStatus.current = 'completed_handled';
            await fireCompletionUX(revealPending);
          }
          clearInterval(profilePollRef.current);
          profilePollRef.current = null;
          return;
        }

        lastQueueStatus.current = status;
      } catch {}
    };
    poll();
    profilePollRef.current = setInterval(poll, 15000);
    return () => { if (profilePollRef.current) clearInterval(profilePollRef.current); };
  }, [user, token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('besume_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    await fetchCsrfToken();
    return userData;
  };

  const signup = async (email, password, name, ref = null, turnstileToken = '') => {
    const payload = { email, password, name };
    if (ref) payload.ref = ref;
    if (turnstileToken) payload.turnstile_token = turnstileToken;
    const response = await axios.post(`${API}/auth/signup`, payload);

    // Handle "email already exists" case (returns message instead of token)
    if (response.data.message && !response.data.token) {
      return { message: response.data.message };
    }

    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('besume_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    await fetchCsrfToken();
    return userData;
  };

  const googleLogin = async (credential, ref = null) => {
    const payload = { credential };
    if (ref) payload.ref = ref;
    const response = await axios.post(`${API}/auth/google`, payload);
    const { token: newToken, user: userData, is_new } = response.data;
    localStorage.setItem('besume_token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    await fetchCsrfToken();
    return { user: userData, is_new };
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Ignore errors on logout
    }
    localStorage.removeItem('besume_token');
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-CSRF-Token'];
    setToken(null);
    setUser(null);
  };

  const updateUser = (updates) => {
    setUser(prev => ({ ...prev, ...updates }));
  };

  const resendVerification = async () => {
    await axios.post(`${API}/auth/resend-verification`);
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      signup,
      googleLogin,
      logout,
      updateUser,
      resendVerification,
      refreshUser: fetchUser,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
